import {
  afterRender,
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AttributeApiService, AttributeSetApiService } from '../../../services';
import {
  APIHelper,
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacButtonComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  CacTabComponent,
  CacTabsComponent,
  FormBuilder,
  formBuilder,
} from '@controladad/ng-base';
import { formControl, Validators } from '@al00x/forms';
import { forkJoin, map, Observable, take } from 'rxjs';
import {
  AttributeCreate,
  AttributeDetail,
  AttributeEntity,
  AttributeTermCreate,
  AttributeTermEntity,
  AttributeTermUpdate,
  AttributeUpdate,
} from '@models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

export interface AttributeDialogData {
  attribute?: AttributeEntity | AttributeDetail;
  focusOnValuesTab?: boolean;
}

/**
 * Global helper function to open attribute dialog with pre-loaded data
 * @param attributeApi - The AttributeApiService instance
 * @param attributeId - The ID of the attribute to edit (undefined for new attribute)
 * @param setLoading - Optional callback to set loading state
 * @param focusOnValuesTab - Whether to focus on the Values tab when opening
 * @returns Observable that emits when dialog is closed (true if submitted, false if cancelled)
 */
export function openAttributeDialog(
  attributeApi: AttributeApiService,
  attributeId: number | undefined,
  setLoading?: (loading: boolean) => void,
  focusOnValuesTab = false
) {

  if (!attributeId) {
    return dialog$.attribute({ focusOnValuesTab }).afterClosed().pipe(
      map(result => result ?? false)
    );
  }

  if (setLoading) {
    setLoading(true);
  }

  return new Observable<boolean>((observer) => {
    attributeApi
      .getDetails(attributeId)
      .pipe(take(1))
      .subscribe({
        next: (attributeDetail) => {
          if (setLoading) {
            setLoading(false);
          }

          dialog$
            .attribute({
              attribute: attributeDetail,
              focusOnValuesTab,
            })
            .afterClosed()
            .pipe(
              take(1),
              map(result => result ?? false)
            )
            .subscribe((result: boolean) => {
              observer.next(result);
              observer.complete();
            });
        },
        error: () => {
          if (setLoading) {
            setLoading(false);
          }
          snackbar$?.show?.('Failed to load attribute details');
          observer.next(false);
          observer.complete();
        },
      });
  });
}

export interface Term {
  termId?: number;
  form: FormBuilder<{
    englishName: string;
    arabicName: string;
    priority: number;
    colorCode: string;
  }>;
}

export type AttributeDialogResult = boolean;

@Component({
  selector: 'lib-attributes-dialog',
  imports: [
    CacDialogLayoutComponent,
    CacFormBuilderComponent,
    CacTabsComponent,
    CacTabComponent,
    NgIf,
    MatIcon,
    CacButtonComponent,
    AsyncPipe,
  ],
  templateUrl: './attribute-dialog.component.html',
  styleUrl: './attribute-dialog.component.scss',
})
export class AttributeDialogComponent extends CacBaseDialogComponent<AttributeDialogData, AttributeDialogResult> implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly attributeApi = inject(AttributeApiService);
  private readonly attributeSetApi = inject(AttributeSetApiService);
  private AttributeSetItems = this.attributeSetApi.getAttributeSetItems();

  @ViewChildren('Term') termElements!: QueryList<ElementRef>;
  @ViewChild(CacTabsComponent) tabs!: CacTabsComponent;

  currentTabIndex = signal(0);
  terms = signal<Term[]>([]);

  termIsExpanded: boolean[] = [];
  isColor = false;
  initialTermsIds: number[] = [];
  shouldScrollToLastTerm = false;

  attributeForm = formBuilder({
    cols: 2,
    inputs: {
      englishName: {
        control: formControl<string>(undefined, Validators.required),
        label: 'English Name',
      },
      arabicName: {
        control: formControl<string>(undefined, Validators.required),
        label: 'Arabic Name',
      },
      priority: {
        label: 'Priority',
        control: formControl<number>(undefined, [Validators.required, Validators.number(1)]),
        colspan: 2,
      },
      isColor: {
        label: 'Is Color',
        control: formControl<boolean>(false),
        type: 'switch',
        colspan: 2,
      },
      attributeSets: {
        label: 'Attribute Sets',
        control: formControl<number[]>(undefined, Validators.required),
        type: 'select',
        multiple: true,
        items: this.AttributeSetItems,
      },
    },
    onInit: (controls) => {
      controls.isColor.valueChanges.pipe(takeUntilDestroyed()).subscribe((isColor) => {
        this.isColorChanged(isColor);
      });
    },
  });

  constructor() {
    super();

    const values = this.data.attribute;
    if (values) {
      // Check if the attribute data is already an AttributeDetail (has 'sets' and 'values' properties)
      if ('sets' in values && 'values' in values) {
        // Data is already loaded, use it directly
        this.initialize(values as AttributeDetail);
      } else {
        // Data is just an AttributeEntity, fetch the full details
        this.attributeApi
          .getDetails(values.id)
          .pipe(take(1))
          .subscribe((d) => this.initialize(d));
      }
    }

    afterRender(() => {
      if (this.shouldScrollToLastTerm) {
        this.shouldScrollToLastTerm = false;
        const elements = this.termElements.toArray();
        const lastElement = elements[elements.length - 1];
        if (lastElement) {
          lastElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  override ngAfterViewInit(): void {
    // Set initial tab index if focusOnValuesTab is true
    if (this.data.focusOnValuesTab && this.tabs) {
      this.tabs.currentIndex.set(1);
    }
  }

  initialize(detail?: AttributeDetail) {
    if (!detail) return;

    this.attributeForm.patchValue({
      englishName: detail.name.english,
      arabicName: detail.name.arabic,
      isColor: detail.isColor,
      priority: detail.priority,
      attributeSets: detail?.sets.map((s) => s.id),
    });

    detail.values.forEach((term) => this.createTermForm(term));
    this.initialTermsIds = detail.values.map((t) => t.id);
  }

  createTermForm(term?: AttributeTermEntity): void {
    const form = formBuilder({
      cols: 1,
      inputs: {
        englishName: {
          control: formControl<string>(term?.value.english, Validators.required),
          label: 'English Name',
        },
        arabicName: {
          control: formControl<string>(term?.value.arabic, Validators.required),
          label: 'Arabic Name',
        },
        priority: {
          control: formControl<number>(term?.priority, Validators.required),
          label: 'Priority',
        },
        colorCode: {
          control: formControl<string>(term?.colorCode, Validators.required),
          label: 'Color',
          hidden: !this.isColor,
        },
      },
    });

    this.terms.update((values) => [...values, { termId: term?.id, form: form }]);
    this.termIsExpanded.push(!term);
    this.shouldScrollToLastTerm = true;
  }

  deleteTerm(index: number) {
    this.terms.update((values) => values.filter((_, idx) => idx !== index));
    this.termIsExpanded.splice(index, 1);
  }

  toggleTerm(index: number): void {
    this.termIsExpanded[index] = !this.termIsExpanded[index];
  }

  isColorChanged(isColor: boolean) {
    this.isColor = isColor;
    this.terms().forEach((t) => t.form.setHidden(!isColor, 'colorCode'));
  }

  onSubmit(e: ButtonClickEvent): void {
    e.setLoading(true);

    const attributeData = this.attributeForm.getValue();
    if (!attributeData) {
      e.setLoading(false);
      this.tabs.tabChange(0);
      return;
    }

    let hasInvalidTerms = false;

    const termsToCreate: AttributeTermCreate[] = [];
    const termsToUpdate: AttributeTermUpdate[] = [];

    this.terms().forEach((term, index) => {
      const termData = term.form.getValue();
      this.termIsExpanded[index] = !termData;

      if (!termData) {
        hasInvalidTerms = true;
      } else {
        const data = {
          id: term.termId,
          value: {
            english: termData.englishName,
            arabic: termData.arabicName,
          },
          priority: termData.priority,
          colorCode: termData.colorCode,
        };

        if (!term.termId) {
          termsToCreate.push(data);
        } else {
          termsToUpdate.push(APIHelper.nestValues(data));
        }
      }
    });

    if (hasInvalidTerms) {
      e.setLoading(false);
      this.tabs.tabChange(1);
      return;
    }

    const attributeId = this.data.attribute?.id;
    const attributeFields = {
      name: {
        english: attributeData.englishName,
        arabic: attributeData.arabicName,
      },
      priority: attributeData.priority,
      isColor: attributeData.isColor,
    };

    if (!attributeId) {
      const payload: AttributeCreate = {
        fields: attributeFields,
        attributeSets: attributeData.attributeSets,
        terms: termsToCreate,
      };

      this.attributeApi
        .create(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.close(true);
            e.setLoading(false);
          },
          error: () => e.setLoading(false),
        });

      return;
    }

    const payload: AttributeUpdate = {
      attributeId,
      fields: APIHelper.nestValues(attributeFields),
      sets: attributeData.attributeSets,
      values: termsToUpdate,
    };

    const idsToDelete = this.initialTermsIds.filter((id) => !termsToUpdate.some((u) => u.id === id));

    forkJoin([
      this.attributeApi.createTermsForAttribute(attributeId, termsToCreate),
      this.attributeApi.update(payload),
      this.attributeApi.deleteTerms(idsToDelete),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.close(true);
          e.setLoading(false);
        },
        error: () => e.setLoading(false),
      });
  }
}


